import React from 'react';
import { motion } from 'framer-motion';
import { Database, Shield, Layers, Zap, GitBranch, Monitor } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

const stack = [
  {
    icon: Layers,
    title: 'React 19 + TypeScript',
    description: 'Modern frontend with strict typing, code splitting, and lazy loading',
    tech: 'Vite, Tailwind CSS, Radix UI',
  },
  {
    icon: Shield,
    title: 'Authentication',
    description: 'Email/password and OAuth sign-in via Neon Auth (Better Auth)',
    tech: 'Neon Auth, OAuth 2.0',
  },
  {
    icon: Database,
    title: 'Neon PostgreSQL',
    description: 'Serverless Postgres with Drizzle ORM and connection pooling via Hyperdrive',
    tech: 'Drizzle ORM, Neon Hyperdrive',
  },
  {
    icon: GitBranch,
    title: 'CI/CD Pipeline',
    description: 'Automated testing, linting, and deployment via GitHub Actions',
    tech: 'GitHub Actions, Semantic Versioning',
  },
  {
    icon: Zap,
    title: 'Edge Deployment',
    description: 'Deployed to Cloudflare Workers for low-latency global delivery',
    tech: 'Cloudflare Workers, Vite Plugin',
  },
  {
    icon: Monitor,
    title: 'Observability',
    description: 'Error tracking and performance monitoring in production',
    tech: 'Sentry, Datadog RUM, PostHog',
  },
];

export const TechnicalArchitectureSection: React.FC = () => {
  return (
    <section className="py-16 bg-[var(--background)] border-t border-[var(--border)]">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-semibold text-[var(--foreground)] mb-3 tracking-tight">
              Foundation for the live matrix
            </h2>
            <p className="text-[var(--muted-foreground)] max-w-2xl mx-auto prose-measure">
              Edge-native stack that makes real-time risk, impact, cost, and cyber visibility
              possible — without the noise.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {stack.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: index * 0.04 }}
                viewport={{ once: true }}
              >
                <Card className="h-full border-[var(--border)] bg-[var(--card)]">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-2.5">
                      <div className="p-1.5 rounded-md bg-[var(--primary)]/10">
                        <item.icon className="h-4 w-4 text-[var(--primary)]" />
                      </div>
                      <h3 className="font-medium text-[var(--foreground)]">{item.title}</h3>
                    </div>
                    <p className="text-sm text-[var(--muted-foreground)] mb-3 leading-relaxed">
                      {item.description}
                    </p>
                    <span className="inline-block px-2 py-0.5 rounded bg-[var(--muted)] text-[var(--muted-foreground)] text-[10px] tracking-wide">
                      {item.tech}
                    </span>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <p className="mt-6 text-center text-xs text-[var(--muted-foreground)]/70">
            All of it wired so the Canvas can show you the full complexity matrix the moment you
            load a template.
          </p>
        </div>
      </div>
    </section>
  );
};
